import React, { useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { User } from '../types';
import { useTranslation } from 'react-i18next';
import { ToastType } from './Toast';

interface NotificationManagerProps {
  user: User | null;
  addToast: (msg: string, type?: ToastType) => void;
}

export const NotificationManager: React.FC<NotificationManagerProps> = ({ user, addToast }) => {
  const { t } = useTranslation('NotificationManager');
  const lastCheckTime = useRef<string>('');

  useEffect(() => {
    if (!user) return;

    // `user` loads asynchronously after the initial render (Supabase auth
    // resolves later), so this can't be a useRef initializer - it would
    // always read the `undefined` localStorage key from that first render
    // and never pick up the real per-user key once `user` becomes available.
    lastCheckTime.current = localStorage.getItem(`lastCheckTime_${user.id}`) || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    const checkNotifications = async () => {
      try {
        const { data: userMemes } = await supabase
          .from('memes')
          .select('id, title')
          .eq('author_id', user.id);

        if (!userMemes || userMemes.length === 0) return;

        const memeIds = userMemes.map(m => m.id);
        const currentTime = new Date().toISOString();

        // Check for new comments
        const { data: newComments } = await supabase
          .from('comments')
          .select('*')
          .in('meme_id', memeIds)
          .gt('created_at', lastCheckTime.current)
          .neq('user_id', user.id)
          .limit(3); // Limit to avoid spam

        if (newComments && newComments.length > 0) {
          newComments.forEach(comment => {
            const meme = userMemes.find(m => m.id === comment.meme_id);
            if (meme) {
               addToast(t('commentedOn', { author: comment.author_name, title: meme.title }), 'info');
            }
          });
        }

        // Check for new likes (meme_votes)
        const { data: newVotes } = await supabase
          .from('meme_votes')
          .select('*')
          .in('meme_id', memeIds)
          .gt('created_at', lastCheckTime.current)
          .neq('user_id', user.id)
          .limit(3); // Limit to avoid spam

        if (newVotes && newVotes.length > 0) {
           newVotes.forEach(vote => {
             const meme = userMemes.find(m => m.id === vote.meme_id);
             if (meme) {
               const message = vote.vote_type === 'funny' ? t('thinksFunny', { title: meme.title }) : t('thinksSkjeta', { title: meme.title });
               addToast(message, 'info');
             }
           });
        }

        lastCheckTime.current = currentTime;
        localStorage.setItem(`lastCheckTime_${user.id}`, currentTime);
      } catch (err) {
        console.error('Failed to check notifications:', err);
      }
    };

    // Check once immediately, then interval
    checkNotifications();
    const interval = setInterval(checkNotifications, 15000); // 15 seconds for responsiveness
    return () => clearInterval(interval);
  }, [user, addToast, t]);

  return null;
};
